import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useFirestoreGallery, GalleryCategory } from '../../hooks/useFirestoreGallery';
import { useCloudinaryUpload } from '../../hooks/useCloudinaryUpload';

const AdminGallery: React.FC = () => {
  const { categories, loading, addImagesToCategory, removeImageFromCategory } = useFirestoreGallery();
  const { uploadMultipleImages, uploading } = useCloudinaryUpload();

  const [selectedCategory, setSelectedCategory] = useState<GalleryCategory | null>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, categoryId: string) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const fileArray = Array.from(files);
      toast.info(`Uploading ${fileArray.length} image(s)... ⏳`);
      const uploadedImages = await uploadMultipleImages(fileArray, `neev-gallery/${categoryId}`);
      await addImagesToCategory(categoryId, uploadedImages);
      toast.success(`${fileArray.length} image(s) added to gallery! 🖼️`);
    } catch (error) {
      toast.error('Failed to upload images. Please try again.');
    }
  };

  const handleRemoveImage = async (categoryId: string, publicId: string) => {
    if (window.confirm('Are you sure you want to remove this image?')) {
      try {
        await removeImageFromCategory(categoryId, publicId);
        toast.success('Image removed successfully! 🗑️');
      } catch (error) {
        toast.error('Failed to remove image. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-brand-light">
      {/* Header */}
      <div className="bg-white shadow-brand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-dark">Gallery Management</h1>
          </div>
          <Link
            to="/admin/dashboard"
            className="text-brand-blue hover:text-brand-pink transition-colors"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <p className="text-brand-blue">Loading categories...</p>
        ) : (
          <>
            {/* Categories List */}
            {!selectedCategory && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                {categories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-3xl p-6 shadow-bubble cursor-pointer hover:shadow-brand transition-all"
                    onClick={() => setSelectedCategory(category)}
                  >
                    <div className={`w-16 h-16 bg-${category.color} rounded-full flex items-center justify-center mx-auto mb-4 text-3xl`}>
                      {category.icon}
                    </div>
                    <h3 className="text-xl font-bold text-brand-dark text-center mb-2">
                      {category.label}
                    </h3>
                    <p className="text-brand-blue text-center">
                      {category.images.length} images
                    </p>
                    <div className="mt-4 text-center">
                      <span className="text-brand-pink font-bold">Click to manage →</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Category Image Management */}
            <AnimatePresence>
              {selectedCategory && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-white rounded-3xl p-8 shadow-bubble"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 bg-${selectedCategory.color} rounded-full flex items-center justify-center text-2xl`}>
                        {selectedCategory.icon}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-brand-dark">
                          {selectedCategory.label}
                        </h2>
                        <p className="text-brand-blue">
                          {selectedCategory.images.length} images
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className="text-brand-blue hover:text-brand-pink font-bold"
                    >
                      ← Back to Categories
                    </button>
                  </div>

                  {/* Upload Section */}
                  <div className="mb-8 p-16 border-2 border-dashed border-brand-blue rounded-bubble">
                    <label className="block text-brand-dark font-bold mb-4 text-center">
                      Upload New Images
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, selectedCategory.id)}
                      className="w-full px-12 py-8 border-2 border-brand-blue rounded-bubble"
                      disabled={uploading}
                    />
                    {uploading && (
                      <p className="text-brand-blue text-center mt-4">Uploading images...</p>
                    )}
                  </div>

                  {/* Current Images */}
                  <div>
                    <h3 className="text-xl font-bold text-brand-dark mb-4">
                      Current Images ({selectedCategory.images.length})
                    </h3>

                    {selectedCategory.images.length === 0 ? (
                      <p className="text-brand-blue text-center py-8">
                        No images yet. Upload some images above!
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {selectedCategory.images.map((image) => (
                          <div key={image.public_id} className="relative group">
                            <img
                              src={image.thumbnail}
                              alt=""
                              className="w-full h-64 object-cover rounded-bubble"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-bubble flex items-center justify-center">
                              <button
                                onClick={() => handleRemoveImage(selectedCategory.id, image.public_id)}
                                className="opacity-0 group-hover:opacity-100 bg-red-500 text-white px-4 py-2 rounded-bubble font-bold hover:bg-red-600 transition-all"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminGallery;

